import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type {
  DayExercise,
  DayExercisePatch,
  Exercise,
} from "../../../shared/src";
import {
  ApiError,
  addDayExercise,
  listDayExercises,
  removeDayExercise,
  reorderDayExercises,
  updateDayExercise,
} from "../api-client";
import { SortableDayExerciseRow } from "./SortableDayExerciseRow";

interface Props {
  dayId: number;
  catalog: Exercise[];
}

interface FormState {
  exerciseId: string;
  sets: string;
  reps: string;
  weight: string;
}

const EMPTY_FORM: FormState = {
  exerciseId: "",
  sets: "",
  reps: "",
  weight: "",
};

function toNumberOrUndefined(s: string): number | undefined {
  if (s.trim() === "") return undefined;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

export function DayExerciseSection({ dayId, catalog }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ["dayExercises", dayId] as const;

  const listQuery = useQuery({
    queryKey,
    queryFn: () => listDayExercises(dayId),
  });

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey });
  }

  const addMutation = useMutation({
    mutationFn: (
      input: Omit<Parameters<typeof addDayExercise>[1], never>,
    ) => addDayExercise(dayId, input),
    onSuccess: () => invalidate(),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => removeDayExercise(id),
    onSuccess: () => invalidate(),
  });

  const updateMutation = useMutation({
    mutationFn: (args: { id: number; patch: DayExercisePatch }) =>
      updateDayExercise(args.id, args.patch),
    onSuccess: () => invalidate(),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => reorderDayExercises(dayId, ids),
    onSuccess: () => invalidate(),
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const assigned = listQuery.data ?? [];
  const assignedIds = new Set(assigned.map((d) => d.exercise_id));
  const available = catalog.filter(
    (e) => !e.archived && !assignedIds.has(e.id),
  );

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const exerciseId = Number(form.exerciseId);
    if (!Number.isInteger(exerciseId) || exerciseId < 1) {
      setError("Pick an exercise.");
      return;
    }
    setError(null);
    try {
      await addMutation.mutateAsync({
        exercise_id: exerciseId,
        target_sets: toNumberOrUndefined(form.sets) ?? null,
        target_reps: toNumberOrUndefined(form.reps) ?? null,
        target_weight_kg: toNumberOrUndefined(form.weight) ?? null,
      });
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(messageFor(err));
    }
  }

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = assigned.map((d) => d.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    queryClient.setQueryData<DayExercise[]>(queryKey, (prev) => {
      if (!prev) return prev;
      const byId = new Map(prev.map((row) => [row.id, row]));
      return next
        .map((targetId, position) => {
          const row = byId.get(targetId);
          return row ? { ...row, position } : null;
        })
        .filter((row): row is DayExercise => row !== null);
    });
    reorderMutation.mutate(next);
  }

  return (
    <div className="mt-3 pl-3 border-l border-slate-800 space-y-3">
      {assigned.length === 0 && (
        <p className="text-xs text-slate-500">No exercises yet.</p>
      )}
      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={assigned.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-1">
            {assigned.map((row) => (
              <li key={row.id}>
                <SortableDayExerciseRow
                  id={row.id}
                  exerciseName={row.exercise_name}
                  disabled={
                    assigned.length < 2 || reorderMutation.isPending
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm">
                      <span>{row.exercise_name}</span>
                      {row.exercise_archived && (
                        <span className="ml-2 text-xs text-amber-400">
                          (catalog archived)
                        </span>
                      )}
                      <span className="ml-2 text-xs text-slate-500">
                        {formatTargets(row)}
                      </span>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setEditingId(editingId === row.id ? null : row.id)
                        }
                        aria-label={`edit ${row.exercise_name}`}
                        className="rounded-md border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removeMutation.mutate(row.id)}
                        disabled={removeMutation.isPending}
                        aria-label={`remove ${row.exercise_name}`}
                        className="rounded-md border border-red-900 px-2 py-0.5 text-xs text-red-300 hover:bg-red-950"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {editingId === row.id && (
                    <EditTargetsForm
                      row={row}
                      pending={updateMutation.isPending}
                      onCancel={() => setEditingId(null)}
                      onSave={async (patch) => {
                        setError(null);
                        try {
                          await updateMutation.mutateAsync({
                            id: row.id,
                            patch,
                          });
                          setEditingId(null);
                        } catch (err) {
                          setError(messageFor(err));
                        }
                      }}
                    />
                  )}
                </SortableDayExerciseRow>
              </li>
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <form
        onSubmit={onAdd}
        className="flex flex-wrap items-end gap-2"
        aria-label={`add exercise to day ${dayId}`}
      >
        <label className="flex-1 min-w-[10rem] block">
          <span className="text-xs text-slate-400">Exercise</span>
          <select
            value={form.exerciseId}
            onChange={(e) => setForm({ ...form, exerciseId: e.target.value })}
            aria-label="exercise"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          >
            <option value="">— pick —</option>
            {available.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.muscle_group ? ` (${e.muscle_group})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Sets</span>
          <input
            type="number"
            min={1}
            step={1}
            value={form.sets}
            onChange={(e) => setForm({ ...form, sets: e.target.value })}
            aria-label="target sets"
            className="mt-1 w-16 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Reps</span>
          <input
            type="number"
            min={1}
            step={1}
            value={form.reps}
            onChange={(e) => setForm({ ...form, reps: e.target.value })}
            aria-label="target reps"
            className="mt-1 w-16 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Weight (kg)</span>
          <input
            type="number"
            min={0}
            step="0.5"
            value={form.weight}
            onChange={(e) => setForm({ ...form, weight: e.target.value })}
            aria-label="target weight kg"
            className="mt-1 w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={addMutation.isPending || form.exerciseId === ""}
          className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
        >
          {addMutation.isPending ? "Adding…" : "Add"}
        </button>
      </form>
      {error && (
        <p className="text-xs text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function EditTargetsForm({
  row,
  pending,
  onCancel,
  onSave,
}: {
  row: DayExercise;
  pending: boolean;
  onCancel: () => void;
  onSave: (patch: DayExercisePatch) => void;
}) {
  const [sets, setSets] = useState(String(row.target_sets ?? ""));
  const [reps, setReps] = useState(String(row.target_reps ?? ""));
  const [weight, setWeight] = useState(String(row.target_weight_kg ?? ""));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    // An emptied field clears that target.
    onSave({
      target_sets: toNumberOrUndefined(sets) ?? null,
      target_reps: toNumberOrUndefined(reps) ?? null,
      target_weight_kg: toNumberOrUndefined(weight) ?? null,
    });
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label={`edit targets for ${row.exercise_name}`}
      className="mt-2 flex flex-wrap items-end gap-2"
    >
      <label className="block">
        <span className="text-xs text-slate-400">Sets</span>
        <input
          type="number"
          min={1}
          step={1}
          value={sets}
          onChange={(e) => setSets(e.target.value)}
          aria-label={`${row.exercise_name} sets`}
          className="mt-1 w-16 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
        />
      </label>
      <label className="block">
        <span className="text-xs text-slate-400">Reps</span>
        <input
          type="number"
          min={1}
          step={1}
          value={reps}
          onChange={(e) => setReps(e.target.value)}
          aria-label={`${row.exercise_name} reps`}
          className="mt-1 w-16 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
        />
      </label>
      <label className="block">
        <span className="text-xs text-slate-400">Weight (kg)</span>
        <input
          type="number"
          min={0}
          step="0.5"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          aria-label={`${row.exercise_name} weight kg`}
          className="mt-1 w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
        />
      </label>
      <button
        type="submit"
        disabled={pending}
        className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
      >
        {pending ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
      >
        Cancel
      </button>
    </form>
  );
}

function formatTargets(row: DayExercise): string {
  const parts: string[] = [];
  if (row.target_sets !== null) parts.push(`${row.target_sets} sets`);
  if (row.target_reps !== null) parts.push(`${row.target_reps} reps`);
  if (row.target_weight_kg !== null) parts.push(`${row.target_weight_kg} kg`);
  return parts.length > 0 ? `· ${parts.join(" × ")}` : "";
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "CONFLICT") return "Already added to this day.";
    if (err.code === "BAD_REQUEST") return err.message;
  }
  return "Something went wrong. Try again.";
}
